import React, { FC, useState, useEffect } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { useAtom } from "jotai";
import Icon from "../../../../Icon";
import RText from "../../../../RText/RText";
import {
  showOddsCheckerOnRacecardAtom,
  showTrackerOnRacecardAtom,
  showFormOnRacecardAtom,
  showTimeformOnRacecardAtom,
  showRiqDataAtom,
} from "../../../../../atoms";
import variables from "../../../../../styles/variables";
import { Runner } from "../../../../../types/races/runner";
import { TrackedHorseComment } from "../../../Tracker/components/TrackedHorseComment";
import { HorseFormTable } from "../../../More/Horse/components/FormTable";
import { getHorseProfile } from "../../../../../client/more/horse";
import {
  Performance,
  HorseTrackerType,
} from "../../../../../types/races/horse";
import SquarePriceButton from "../../../../Buttons/SquarePriceButton";
import { formatCountryCode } from "../../../../../utils/race";
import TimeformIcon from "../../../../Icon/TimeformIcon";
import { sortOddsByPriority } from "../../../../../client/odds";
import { OddBookmakers } from "../../../../../types/bookmaker";
import RScrollView from "../../../../RScrollView";
import RaceiQIconBar from "../../../../RaceIQ/shared/IconBar/IconBar";

type Props = {
  runner: Runner;
  displaysPremiumData: boolean;
  bookmakers: OddBookmakers;
  refreshListOfTrackedHorses: Function;
  trackedHorse?: HorseTrackerType;
  index?: number;
  firstRunnerWithRiQPromptId?: number;
};

/**
 * @file ExpandedRacecardRunnerRow.tsx
 * @description Displays detailed horse runner information in the racecard, including form, tracker notes, odds, and premium data like Timeform.
 * Displays an expanded view of a horse runner on the racecard,
 * including form history, tracking controls, odds, and premium data.
 *
 * @param {Runner} runner - The horse runner data to display.
 * @param {boolean} displaysPremiumData - Whether to show premium data like Timeform badges.
 * @param {OddBookmakers} bookmakers - The list of available bookmakers for odds display.
 * @param {Function} refreshListOfTrackedHorses - Callback to refresh the tracked horses list.
 * @param {HorseTrackerType} [trackedHorse] - The tracked horse data, if any.
 * @param {boolean} [isExpanded=false] - Whether the row is expanded to show more details.
 * @param {number} [firstRunnerWithRiQPromptId] - The id of the first runner with a RaceiQ prompt, if applicable.
 * @returns {JSX.Element} - The rendered component displaying the expanded racecard runner row.
 */
const ExpandedRacecardRunnerRow: FC<Props> = ({
  runner,
  displaysPremiumData,
  bookmakers,
  refreshListOfTrackedHorses,
  trackedHorse,
  index,
  firstRunnerWithRiQPromptId,
}) => {
  const [showTracker] = useAtom(showTrackerOnRacecardAtom);
  const [showForm] = useAtom(showFormOnRacecardAtom);
  const [showTimeform] = useAtom(showTimeformOnRacecardAtom);

  const [error, setError] = useState();
  const [formRecords, setFormRecords] = useState<Performance[]>();
  const [formRecordsLoading, setFormRecordsLoading] = useState(true);
  const [showOddsChecker] = useAtom(showOddsCheckerOnRacecardAtom);
  const [showRaceIq] = useAtom(showRiqDataAtom);

  useEffect(() => {
    fetchHorseData();
  }, []);

  /**
   * Fetches the horse form data asynchronously and updates state.
   */
  const fetchHorseData = async () => {
    try {
      const response = await getHorseProfile(runner.horse.slug);
      setFormRecords(response.horse.performances);
    } catch (error) {
      setError(error);
    } finally {
      setFormRecordsLoading(false);
    }
  };

  return (
    <View style={{ paddingTop: 16 }}>
      {showRaceIq &&
        runner?.prompts &&
        !!Object.keys(runner?.prompts).length && (
          <View style={{ marginBottom: 16 }}>
            <RaceiQIconBar
              key={`iconbar-${runner.horse.name}-${index}`}
              firstRunner={firstRunnerWithRiQPromptId === runner.horse_id}
              prompts={runner.prompts}
            />
          </View>
        )}
      {showTimeform ? (
        <View style={{ paddingBottom: 16 }}>
          <View style={[styles.horseDataContainer]}>
            <View style={styles.horseData}>
              <RText color="grey">Col./Sex: </RText>
              <RText weight="semiBold">
                {runner.horse.colors[0]} {runner.horse.gender}
              </RText>
            </View>
            <View style={styles.horseData}>
              <RText color="grey">S: </RText>
              {!!runner.horse.sire_name && (
                <RText weight="semiBold">
                  {runner.horse.sire_name}{" "}
                  {!!runner.horse.country_code?.racing &&
                    runner.horse.country_code?.racing !== "GBR" &&
                    `(${formatCountryCode(runner.horse.country_code?.racing)})`}
                </RText>
              )}
            </View>
            <View style={styles.horseData}>
              <RText color="grey">D: </RText>
              {!!runner.horse.dam_name && (
                <RText weight="semiBold">
                  {runner.horse.dam_name}{" "}
                  {!!runner.horse.country_code?.racing &&
                    runner.horse.country_code?.racing !== "GBR" &&
                    `(${formatCountryCode(runner.horse.country_code?.racing)})`}
                </RText>
              )}
            </View>
          </View>
          <View style={styles.timeFormText}>
            <View
              style={{
                height: 25,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                name="timeform"
                style={{
                  width: 54.83,
                  height: 12,
                  alignItems: "center",
                  alignSelf: "center",
                  marginRight: 5,
                }}
                size="xxlarge"
              />
            </View>
            <View
              style={{ marginRight: 5, flexDirection: "row", marginBottom: 2 }}
            >
              {[...Array(5)].map((_, i) => (
                <RText
                  key={i}
                  style={{ marginRight: i === 4 ? 5 : 1 }}
                  color={
                    i + 1 <= runner.timeform_star_rating ? "black" : "grey"
                  }
                >
                  ★
                </RText>
              ))}
            </View>
            <RText>
              {runner.analyst_comment}{" "}
              {displaysPremiumData &&
                runner.icon_badges?.map((badge, i) => (
                  <TimeformIcon badge={badge} key={i} />
                ))}
            </RText>
          </View>
        </View>
      ) : null}
      {showForm ? (
        <HorseFormTable
          type="horse"
          form={formRecords}
          loading={formRecordsLoading}
          tableTitle={false}
          expandFormBtn
          limit={6}
          displayedOn="racecard"
        />
      ) : null}

      {showTracker && (
        <View style={{ paddingBottom: 16 }}>
          <TrackedHorseComment
            horseId={runner.horse?.id || runner.id}
            horseName={runner.horse.name}
            isExpanded={showTracker}
            withControls
            refreshListOfTrackedHorses={refreshListOfTrackedHorses}
            trackedHorse={trackedHorse}
          />
        </View>
      )}
      {!!runner?.odds && showOddsChecker && (
        <Odds runner={runner} bookmakers={bookmakers} />
      )}
    </View>
  );
};

/**
 * Displays odds for the given runner.
 * Uses either a grid layout or horizontal scroll view depending on screen size.
 *
 * @component
 * @param {{ runner: Runner; bookmakers: OddBookmakers }} props
 * @returns {JSX.Element}
 */
const Odds = ({
  runner,
  bookmakers,
}: {
  runner: Runner;
  bookmakers: OddBookmakers;
}) => {
  const scrollRef = React.useRef<ScrollView>(null);

  return (
    <View style={{ paddingBottom: 16 }}>
      <RScrollView
        scrollRef={scrollRef}
        elementDistance={60 + variables.spacing.small}
        horizontal
        showsHorizontalScrollIndicator={false}
        childrenWidth={60 * runner.odds?.length}
      >
        {sortOddsByPriority(runner.odds, bookmakers).map((odd, i) => {
          return (
            <SquarePriceButton
              key={i}
              odd={odd}
              size="large"
              bold
              onPress={() => {}}
              bookmakers={bookmakers}
              style={
                i !== runner.odds.length - 1 && {
                  marginRight: variables.spacing.small,
                }
              }
            />
          );
        })}
      </RScrollView>
    </View>
  );
};

export default ExpandedRacecardRunnerRow;

const { spacing } = variables;
const styles = StyleSheet.create({
  horseDataContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
  },
  horseData: {
    flexDirection: "row",
    margin: spacing.xxxsmall,
  },
  timeFormText: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    alignItems: "center",
  },
});
